import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

interface BracketTemplate {
  id: string;
  name: string;
  category?: string;
  format: string;
  playerCount: number;
  structure: string;
  description?: string;
  currentStage?: number;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

interface MixedStage {
  type: 'ROUND_ROBIN' | 'SINGLE_ELIMINATION';
  name: string;
  templateId?: string; // ID существующего шаблона
  groups?: number;
  playersPerGroup?: number;
  qualifiedFromGroups?: number;
  directFinal?: boolean; // Прямой выход в финал без промежуточных раундов
  thirdPlaceMatch?: boolean; // Включить игру за 3-е место
}

type PositionServiceCodes = Record<string, string>;

const BracketTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<BracketTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<BracketTemplate | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    format: 'SINGLE_ELIMINATION',
    playerCount: 8,
    structure: '', // Структура генерируется автоматически
    description: ''
  });
  const [mixedStages, setMixedStages] = useState<MixedStage[]>([]);
  const [positionServiceCodes, setPositionServiceCodes] = useState<PositionServiceCodes>({});

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const data = await apiGet<BracketTemplate[]>('/bracket-templates');
      setTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Ошибка загрузки шаблонов:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      format: 'SINGLE_ELIMINATION',
      playerCount: 8,
      structure: '',
      description: ''
    });
    setMixedStages([]);
    setPositionServiceCodes({});
    setShowForm(true);
  };

  // Генерирует JSON структуру из mixedStages
  const generateStructureFromStages = (stages: MixedStage[], serviceCodes?: PositionServiceCodes): string => {
    return JSON.stringify(
      {
        stages,
        ...(serviceCodes && Object.keys(serviceCodes).length > 0
          ? { positionServiceCodes: serviceCodes }
          : {})
      },
      null,
      2
    );
  };

  // Добавление нового этапа
  const handleAddStage = () => {
    setMixedStages([...mixedStages, {
      type: 'ROUND_ROBIN',
      name: '',
      groups: 2,
      playersPerGroup: 4
    }]);
  };

  // Рассчитывает общее количество игроков для Mixed формата
  const calculateMixedPlayerCount = (stages: MixedStage[]): number => {
    // Количество игроков определяется первым этапом (Round Robin)
    if (stages.length > 0 && stages[0].type === 'ROUND_ROBIN') {
      const firstStage = stages[0];
      if (firstStage.groups && firstStage.playersPerGroup) {
        return firstStage.groups * firstStage.playersPerGroup;
      }
    }
    return 0;
  };

  // Генерирует понятное название для Mixed формата
  const generateMixedName = (stages: MixedStage[]): string => {
    if (stages.length === 0) return '';
    
    const parts: string[] = [];
    
    // Группируем этапы Round Robin
    const roundRobinStages = stages.filter(s => s.type === 'ROUND_ROBIN');
    if (roundRobinStages.length > 0) {
      const rrParts: string[] = [];
      roundRobinStages.forEach(stage => {
        if (stage.groups && stage.playersPerGroup) {
          if (stage.groups === 1) {
            rrParts.push(`Round Robin (${stage.playersPerGroup} игроков)`);
          } else {
            rrParts.push(`${stage.groups} группы Round Robin по ${stage.playersPerGroup} игроков`);
          }
        }
      });
      if (rrParts.length > 0) {
        parts.push(rrParts.join(' + '));
      }
    }
    
    // Добавляем этапы Single Elimination
    const eliminationStages = stages.filter(s => s.type === 'SINGLE_ELIMINATION');
    if (eliminationStages.length > 0) {
      eliminationStages.forEach(stage => {
        if (stage.qualifiedFromGroups) {
          const totalQualified = roundRobinStages.length > 0 && roundRobinStages[0].groups 
            ? stage.qualifiedFromGroups * roundRobinStages[0].groups 
            : stage.qualifiedFromGroups;
          parts.push(`→ Single Elimination (${totalQualified} игроков)`);
        } else {
          parts.push('→ Single Elimination');
        }
      });
    }
    
    return parts.join(' ');
  };

  // Обновление этапа
  const handleUpdateStage = (index: number, updates: Partial<MixedStage>) => {
    const newStages = [...mixedStages];
    newStages[index] = { ...newStages[index], ...updates };
    setMixedStages(newStages);
    
    // Автоматически обновляем структуру JSON
    if (formData.format === 'MIXED') {
      const newStructure = generateStructureFromStages(newStages, positionServiceCodes);
      const calculatedPlayerCount = calculateMixedPlayerCount(newStages);
      const generatedName = generateMixedName(newStages);
      
      // Автоматически обновляем название, если оно было пустым или сгенерированным
      const currentName = formData.name;
      const shouldUpdateName = !currentName || currentName === generateMixedName(mixedStages);
      
      setFormData({ 
        ...formData, 
        structure: newStructure, 
        playerCount: calculatedPlayerCount,
        name: shouldUpdateName ? generatedName : currentName
      });
    }
  };

  // Удаление этапа
  const handleRemoveStage = (index: number) => {
    const newStages = mixedStages.filter((_, i) => i !== index);
    setMixedStages(newStages);
    
    if (formData.format === 'MIXED') {
      const newStructure = generateStructureFromStages(newStages, positionServiceCodes);
      const calculatedPlayerCount = calculateMixedPlayerCount(newStages);
      const generatedName = generateMixedName(newStages);
      
      // Автоматически обновляем название, если оно было сгенерированным
      const currentName = formData.name;
      const shouldUpdateName = !currentName || currentName === generateMixedName(mixedStages);
      
      setFormData({ 
        ...formData, 
        structure: newStructure, 
        playerCount: calculatedPlayerCount,
        name: shouldUpdateName ? generatedName : currentName
      });
    }
  };

  const handleEdit = (template: BracketTemplate) => {
    setEditingTemplate(template);
    const structure = typeof template.structure === 'string' 
      ? template.structure 
      : JSON.stringify(template.structure, null, 2);
    
    setFormData({
      name: template.name,
      format: template.format,
      playerCount: template.playerCount,
      structure: structure,
      description: template.description || ''
    });
    
    // Если Mixed формат, парсим структуру в stages
    try {
      const parsed = JSON.parse(structure);
      const rawCodes =
        parsed && typeof parsed === 'object' && parsed.positionServiceCodes && typeof parsed.positionServiceCodes === 'object'
          ? parsed.positionServiceCodes
          : {};
      const normalizedCodes: PositionServiceCodes = {};
      Object.entries(rawCodes as Record<string, unknown>).forEach(([position, code]) => {
        if (typeof code !== 'string') return;
        const trimmed = code.trim();
        if (!trimmed) return;
        normalizedCodes[String(position)] = trimmed;
      });
      setPositionServiceCodes(normalizedCodes);
    } catch {
      setPositionServiceCodes({});
    }

    if (template.format === 'MIXED') {
      try {
        const parsed = JSON.parse(structure);
        if (parsed.stages && Array.isArray(parsed.stages)) {
          setMixedStages(parsed.stages);
        } else {
          setMixedStages([]);
        }
      } catch {
        setMixedStages([]);
      }
    } else {
      setMixedStages([]);
    }
    
    setShowForm(true);
  };

  const validateStructure = (stages: MixedStage[], format: string): string | null => {
    if (format === 'MIXED') {
      if (!stages || stages.length === 0) {
        return 'Для Mixed формата необходимо добавить хотя бы один этап';
      }
      
      for (let i = 0; i < stages.length; i++) {
        const stage = stages[i];
        
        if (!stage.type || !['ROUND_ROBIN', 'SINGLE_ELIMINATION'].includes(stage.type)) {
          return `Этап ${i + 1}: должен иметь type "ROUND_ROBIN" или "SINGLE_ELIMINATION"`;
        }
        
        if (stage.type === 'ROUND_ROBIN') {
          if (!stage.groups || stage.groups < 1) {
            return `Этап ${i + 1}: необходимо указать количество групп (groups)`;
          }
          if (!stage.playersPerGroup || stage.playersPerGroup < 2) {
            return `Этап ${i + 1}: необходимо указать игроков в группе (playersPerGroup)`;
          }
        }
        
        if (stage.type === 'SINGLE_ELIMINATION') {
          if (!stage.qualifiedFromGroups || stage.qualifiedFromGroups < 1) {
            return `Этап ${i + 1}: необходимо указать сколько игроков проходит из каждой группы (qualifiedFromGroups)`;
          }
          
          // Валидация для режима прямого финала
          if (stage.directFinal === true) {
            const roundRobinStage = stages.find((s) => s.type === 'ROUND_ROBIN');
            const groupsCount = Math.max(1, Number(roundRobinStage?.groups || 1));
            const totalQualified = Number(stage.qualifiedFromGroups || 0) * groupsCount;

            if (stage.thirdPlaceMatch === true && totalQualified < 4) {
              return `Этап ${i + 1}: для финала с игрой за 3-е место необходимо минимум 4 игрока (сейчас: ${totalQualified})`;
            }
            if (stage.thirdPlaceMatch !== true && totalQualified < 2) {
              return `Этап ${i + 1}: для финала необходимо минимум 2 игрока (сейчас: ${totalQualified})`;
            }
          }
        }
      }
    }
    
    return null;
  };

  // Генерирует структуру для Round Robin
  const generateRoundRobinStructure = (playerCount: number, serviceCodes?: PositionServiceCodes): string => {
    return JSON.stringify({
      type: 'ROUND_ROBIN',
      playerCount,
      matches: (playerCount * (playerCount - 1)) / 2,
      ...(serviceCodes && Object.keys(serviceCodes).length > 0 ? { positionServiceCodes: serviceCodes } : {})
    }, null, 2);
  };

  // Генерирует структуру для Single Elimination
  const generateSingleEliminationStructure = (playerCount: number, serviceCodes?: PositionServiceCodes): string => {
    const normalizedPlayerCount = Number(playerCount);
    if (!Number.isFinite(normalizedPlayerCount) || normalizedPlayerCount < 2) {
      return JSON.stringify({
        type: 'SINGLE_ELIMINATION',
        playerCount: 0,
        rounds: [],
        ...(serviceCodes && Object.keys(serviceCodes).length > 0 ? { positionServiceCodes: serviceCodes } : {})
      }, null, 2);
    }

    // В текущей логике проекта первый раунд имеет наибольшее значение round.
    const roundsCount = Math.ceil(Math.log2(normalizedPlayerCount));
    const roundsArray: Array<{ round: number; name: string; matches: number }> = [];

    for (let i = roundsCount; i > 0; i--) {
      const matchesInRound = Math.pow(2, i - 1);
      let roundName = '';
      if (matchesInRound === 1) {
        roundName = 'Финал';
      } else if (matchesInRound === 2) {
        roundName = 'Полуфинал';
      } else if (matchesInRound === 4) {
        roundName = '1/4 финала';
      } else if (matchesInRound === 8) {
        roundName = '1/8 финала';
      } else {
        roundName = `1/${matchesInRound * 2} финала`;
      }

      roundsArray.push({
        round: i,
        name: roundName,
        matches: matchesInRound
      });
    }
    
    return JSON.stringify({
      type: 'SINGLE_ELIMINATION',
      playerCount: normalizedPlayerCount,
      rounds: roundsArray,
      ...(serviceCodes && Object.keys(serviceCodes).length > 0 ? { positionServiceCodes: serviceCodes } : {})
    }, null, 2);
  };

  const handleSave = async () => {
    try {
      let structureToSave = formData.structure;
      let playerCountToSave = formData.playerCount;
      
      // Для Mixed формата валидируем этапы
      if (formData.format === 'MIXED') {
        const validationError = validateStructure(mixedStages, formData.format);
        if (validationError) {
          alert(`Ошибка валидации: ${validationError}`);
          return;
        }
        
        // Генерируем структуру из этапов
        structureToSave = generateStructureFromStages(mixedStages, positionServiceCodes);
        
        // Автоматически рассчитываем количество игроков
        playerCountToSave = calculateMixedPlayerCount(mixedStages);
        
        if (playerCountToSave === 0) {
          alert('Ошибка: Не удалось рассчитать количество игроков. Убедитесь, что первый этап Round Robin настроен правильно.');
          return;
        }
      } else if (formData.format === 'ROUND_ROBIN') {
        // Автоматически генерируем структуру для Round Robin
        structureToSave = generateRoundRobinStructure(formData.playerCount, positionServiceCodes);
      } else if (formData.format === 'SINGLE_ELIMINATION') {
        // Автоматически генерируем структуру для Single Elimination
        structureToSave = generateSingleEliminationStructure(Number(formData.playerCount), positionServiceCodes);
      }

      const data = {
        ...formData,
        category: null, // Категория не используется
        structure: structureToSave,
        playerCount: Number(playerCountToSave)
      };

      if (editingTemplate) {
        await apiPut(`/bracket-templates/${editingTemplate.id}`, data);
      } else {
        await apiPost('/bracket-templates', data);
      }

      setShowForm(false);
      setEditingTemplate(null);
      setMixedStages([]);
      setPositionServiceCodes({});
      fetchTemplates();
    } catch (error: any) {
      alert(`Ошибка сохранения: ${error.message || 'Неизвестная ошибка'}`);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Вы уверены, что хотите удалить этот шаблон?')) {
      return;
    }

    try {
      await apiDelete(`/bracket-templates/${id}`);
      fetchTemplates();
    } catch (error: any) {
      alert(`Ошибка удаления: ${error.message || 'Неизвестная ошибка'}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Справочник сеток</h1>
        <button
          onClick={handleCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          + Создать шаблон
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Название
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Формат
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Игроков
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {templates.map((template) => (
              <tr key={template.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {template.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center">
                    {template.format === 'SINGLE_ELIMINATION' ? (
                      <span>Single Elimination</span>
                    ) : template.format === 'ROUND_ROBIN' ? (
                      <span className="flex items-center">
                        <span className="mr-1">Round Robin</span>
                        <span className="text-xs text-blue-600" title="Групповой формат: каждый играет с каждым">
                          (группа)
                        </span>
                      </span>
                    ) : template.format === 'MIXED' ? (
                      <span>Mixed</span>
                    ) : (
                      <span>{template.format}</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {template.playerCount}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleEdit(template)}
                    className="text-blue-600 hover:text-blue-900 mr-4"
                  >
                    Редактировать
                  </button>
                  <button
                    onClick={() => handleDelete(template.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {templates.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Шаблоны сеток не найдены
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingTemplate ? 'Редактировать шаблон' : 'Создать шаблон'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название *
                  {formData.format === 'MIXED' && (
                    <span className="text-xs text-gray-500 ml-2">(генерируется автоматически, можно изменить)</span>
                  )}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
                {formData.format === 'MIXED' && mixedStages.length > 0 && (
                  <p className="text-xs text-blue-600 mt-1">
                    💡 Название автоматически обновляется при изменении этапов
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Формат *
                  </label>
                  <select
                    value={formData.format}
                    onChange={(e) => {
                      const newFormat = e.target.value;
                      // Если выбран Mixed, инициализируем пустой массив этапов
                      if (newFormat === 'MIXED') {
                        if (mixedStages.length === 0) {
                          // Если нет этапов, добавляем один по умолчанию
                          const defaultStages: MixedStage[] = [{
                            type: "ROUND_ROBIN",
                            name: "Групповой этап",
                            groups: 2,
                            playersPerGroup: 4
                          }];
                          setMixedStages(defaultStages);
                          const calculatedPlayerCount = calculateMixedPlayerCount(defaultStages);
                          const generatedName = generateMixedName(defaultStages);
                          setFormData({ 
                            ...formData, 
                            format: newFormat,
                            structure: generateStructureFromStages(defaultStages, positionServiceCodes),
                            playerCount: calculatedPlayerCount,
                            name: generatedName
                          });
                        } else {
                          // Обновляем структуру из существующих этапов и пересчитываем игроков
                          const calculatedPlayerCount = calculateMixedPlayerCount(mixedStages);
                          const generatedName = generateMixedName(mixedStages);
                          setFormData({ 
                            ...formData, 
                            format: newFormat,
                            structure: generateStructureFromStages(mixedStages, positionServiceCodes),
                            playerCount: calculatedPlayerCount,
                            name: generatedName
                          });
                        }
                      } else {
                        // Для других форматов очищаем этапы
                        setMixedStages([]);
                        setFormData({ ...formData, format: newFormat });
                      }
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="SINGLE_ELIMINATION">Single Elimination</option>
                    <option value="ROUND_ROBIN">Round Robin</option>
                    <option value="MIXED">Mixed</option>
                  </select>
                  {formData.format === 'ROUND_ROBIN' && (
                    <p className="text-xs text-blue-600 mt-1">
                      💡 Round Robin - групповой формат, где каждый игрок играет с каждым
                    </p>
                  )}
                  {formData.format === 'SINGLE_ELIMINATION' && (
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Single Elimination - плей-офф на выбывание
                    </p>
                  )}
                  {formData.format === 'MIXED' && (
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Mixed - смешанный формат (групповой этап + плей-офф)
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Количество игроков *
                  </label>
                  {formData.format === 'MIXED' ? (
                    <>
                      <input
                        type="number"
                        value={calculateMixedPlayerCount(mixedStages) || formData.playerCount}
                        readOnly
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 cursor-not-allowed"
                        min="2"
                      />
                      {mixedStages.length > 0 && mixedStages[0].type === 'ROUND_ROBIN' && mixedStages[0].groups && mixedStages[0].playersPerGroup && (
                        <p className="text-xs text-blue-600 mt-1">
                          💡 Рассчитывается автоматически: {mixedStages[0].groups} групп × {mixedStages[0].playersPerGroup} игроков = {calculateMixedPlayerCount(mixedStages)} игроков
                        </p>
                      )}
                      {(!mixedStages.length || !mixedStages[0] || mixedStages[0].type !== 'ROUND_ROBIN' || !mixedStages[0].groups || !mixedStages[0].playersPerGroup) && (
                        <p className="text-xs text-gray-500 mt-1">
                          Укажите параметры первого этапа Round Robin для расчета количества игроков
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <input
                        type="number"
                        value={formData.playerCount}
                        onChange={(e) => setFormData({ ...formData, playerCount: Number(e.target.value) })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        min="2"
                        required
                      />
                      {formData.format === 'ROUND_ROBIN' && formData.playerCount >= 2 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Будет создано {Math.floor((formData.playerCount * (formData.playerCount - 1)) / 2)} матчей
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>

              {formData.format === 'MIXED' ? (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Этапы турнира *
                    </label>
                    <button
                      type="button"
                      onClick={handleAddStage}
                      className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                    >
                      + Добавить этап
                    </button>
                  </div>
                  <div className="space-y-3">
                    {mixedStages.map((stage, index) => (
                      <div key={index} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-700">Этап {index + 1}</span>
                            {index > 0 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newStages = [...mixedStages];
                                  [newStages[index - 1], newStages[index]] = [newStages[index], newStages[index - 1]];
                                  setMixedStages(newStages);
                                  setFormData({ ...formData, structure: generateStructureFromStages(newStages, positionServiceCodes) });
                                }}
                                className="text-xs text-blue-600 hover:text-blue-800"
                                title="Переместить вверх"
                              >
                                ↑
                              </button>
                            )}
                            {index < mixedStages.length - 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newStages = [...mixedStages];
                                  [newStages[index], newStages[index + 1]] = [newStages[index + 1], newStages[index]];
                                  setMixedStages(newStages);
                                  setFormData({ ...formData, structure: generateStructureFromStages(newStages, positionServiceCodes) });
                                }}
                                className="text-xs text-blue-600 hover:text-blue-800"
                                title="Переместить вниз"
                              >
                                ↓
                              </button>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveStage(index)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            ✕ Удалить
                          </button>
                        </div>
                        
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Использовать шаблон
                            </label>
                            <select
                              value={stage.templateId || ''}
                              onChange={(e) => {
                                const templateId = e.target.value;
                                if (templateId) {
                                  // Находим шаблон по ID
                                  const template = templates.find(t => t.id === templateId);
                                  if (template) {
                                    const updates: Partial<MixedStage> = {
                                      templateId,
                                      type: template.format === 'ROUND_ROBIN' ? 'ROUND_ROBIN' : 'SINGLE_ELIMINATION',
                                      name: template.name
                                    };
                                    
                                    // Если Round Robin, подставляем параметры из шаблона
                                    if (template.format === 'ROUND_ROBIN') {
                                      // Для Round Robin используем playerCount как playersPerGroup
                                      updates.playersPerGroup = template.playerCount;
                                      // Если нет groups, устанавливаем 1 по умолчанию
                                      if (!stage.groups) {
                                        updates.groups = 1;
                                      }
                                      updates.qualifiedFromGroups = undefined;
                                    } else if (template.format === 'SINGLE_ELIMINATION') {
                                      // Для Single Elimination очищаем параметры Round Robin
                                      updates.groups = undefined;
                                      updates.playersPerGroup = undefined;
                                      // Если нет qualifiedFromGroups, устанавливаем 1 по умолчанию
                                      if (!stage.qualifiedFromGroups) {
                                        updates.qualifiedFromGroups = 1;
                                      }
                                    }
                                    
                                    handleUpdateStage(index, updates);
                                  }
                                } else {
                                  // Если шаблон не выбран, сбрасываем тип и параметры
                                  handleUpdateStage(index, { 
                                    templateId: undefined,
                                    type: 'ROUND_ROBIN',
                                    groups: 2,
                                    playersPerGroup: 4,
                                    qualifiedFromGroups: undefined
                                  });
                                }
                              }}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            >
                              <option value="">Выберите шаблон или настройте вручную...</option>
                              {templates
                                .filter(t => t.format === 'ROUND_ROBIN' || t.format === 'SINGLE_ELIMINATION')
                                .map(t => (
                                  <option key={t.id} value={t.id}>
                                    {t.name} ({t.format === 'ROUND_ROBIN' ? `${t.playerCount} игроков` : `${t.playerCount} игроков`})
                                  </option>
                                ))}
                            </select>
                            {stage.templateId && (
                              <p className="text-xs text-blue-600 mt-1">
                                Тип этапа: {stage.type === 'ROUND_ROBIN' ? 'Round Robin (групповой)' : 'Single Elimination (плей-офф)'}
                              </p>
                            )}
                          </div>

                          {!stage.templateId && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Тип этапа *
                              </label>
                              <select
                                value={stage.type}
                                onChange={(e) => {
                                  const newType = e.target.value as 'ROUND_ROBIN' | 'SINGLE_ELIMINATION';
                                  const updates: Partial<MixedStage> = { type: newType };
                                  
                                  if (newType === 'ROUND_ROBIN') {
                                    updates.groups = 2;
                                    updates.playersPerGroup = 4;
                                    updates.qualifiedFromGroups = undefined;
                                  } else {
                                    updates.qualifiedFromGroups = 2;
                                    updates.groups = undefined;
                                    updates.playersPerGroup = undefined;
                                  }
                                  
                                  handleUpdateStage(index, updates);
                                }}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                              >
                                <option value="ROUND_ROBIN">Round Robin (групповой)</option>
                                <option value="SINGLE_ELIMINATION">Single Elimination (плей-офф)</option>
                              </select>
                            </div>
                          )}

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Название этапа
                            </label>
                            <input
                              type="text"
                              value={stage.name}
                              onChange={(e) => handleUpdateStage(index, { name: e.target.value })}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                              placeholder={stage.type === 'ROUND_ROBIN' ? 'Групповой этап' : 'Плей-офф'}
                            />
                          </div>

                          {stage.type === 'ROUND_ROBIN' && (
                            <>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Количество групп *
                                  </label>
                                  <input
                                    type="number"
                                    value={stage.groups || ''}
                                    onChange={(e) => handleUpdateStage(index, { groups: Number(e.target.value) })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                    min="1"
                                    required
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Игроков в группе *
                                    {stage.templateId && (
                                      <span className="text-xs text-gray-500 ml-1">(из шаблона)</span>
                                    )}
                                  </label>
                                  <input
                                    type="number"
                                    value={stage.playersPerGroup || ''}
                                    onChange={(e) => handleUpdateStage(index, { playersPerGroup: Number(e.target.value) })}
                                    className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm ${
                                      stage.templateId ? 'bg-gray-100 cursor-not-allowed' : ''
                                    }`}
                                    min="2"
                                    required
                                    readOnly={!!stage.templateId}
                                  />
                                </div>
                              </div>
                            </>
                          )}

                          {stage.type === 'SINGLE_ELIMINATION' && (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Проходит из каждой группы *
                                </label>
                                <input
                                  type="number"
                                  value={stage.qualifiedFromGroups || ''}
                                  onChange={(e) => handleUpdateStage(index, { qualifiedFromGroups: Number(e.target.value) })}
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                  min="1"
                                  required
                                  placeholder="Сколько игроков проходит из каждой группы"
                                />
                              </div>
                              
                              <div className="space-y-2 pt-2 border-t border-gray-200">
                                <div className="flex items-start space-x-2">
                                  <input
                                    type="checkbox"
                                    id={`directFinal-${index}`}
                                    checked={stage.directFinal || false}
                                    onChange={(e) => handleUpdateStage(index, { 
                                      directFinal: e.target.checked 
                                    })}
                                    className="mt-0.5 rounded border-gray-300"
                                  />
                                  <label htmlFor={`directFinal-${index}`} className="text-xs text-gray-700 cursor-pointer">
                                    <div className="font-medium">Прямой выход в финал</div>
                                    <div className="text-gray-500">Без промежуточных раундов (места из группы напрямую определяют финальные матчи)</div>
                                  </label>
                                </div>
                                
                                <div className="flex items-start space-x-2">
                                  <input
                                    type="checkbox"
                                    id={`thirdPlace-${index}`}
                                    checked={stage.thirdPlaceMatch || false}
                                    onChange={(e) => handleUpdateStage(index, { 
                                      thirdPlaceMatch: e.target.checked 
                                    })}
                                    className="mt-0.5 rounded border-gray-300"
                                  />
                                  <label htmlFor={`thirdPlace-${index}`} className="text-xs text-gray-700 cursor-pointer">
                                    <div className="font-medium">Игра за 3-е место</div>
                                    <div className="text-gray-500">Добавить матч между проигравшими в полуфинале (или 3-м и 4-м местами)</div>
                                  </label>
                                </div>
                                
                                {stage.directFinal && (
                                  <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                                    <p className="text-xs text-blue-800">
                                      💡 <strong>Режим прямого финала:</strong> Места из группового этапа напрямую определяют финальные матчи
                                      {stage.thirdPlaceMatch ? ' (Финал: 1-е vs 2-е место, Матч за 3-е место: 3-е vs 4-е место)' : ' (Финал: 1-е vs 2-е место)'}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {mixedStages.length === 0 && (
                      <div className="text-center py-4 text-gray-500 text-sm border border-dashed border-gray-300 rounded-lg">
                        Нет этапов. Нажмите "+ Добавить этап" для добавления.
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Описание
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Служебные коды позиций
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Если заполнить код, на странице жеребьёвки будет показано: «Позиция N - КОД». Если не заполнено — останется только «Позиция N».
                </p>
                <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-2">
                  {Array.from({ length: Math.max(0, Number(formData.playerCount) || 0) }, (_, i) => i + 1).map((position) => (
                    <div key={position} className="grid grid-cols-[140px_1fr] gap-2 items-center">
                      <span className="text-sm text-gray-700">Позиция {position}</span>
                      <input
                        type="text"
                        value={positionServiceCodes[String(position)] || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setPositionServiceCodes((prev) => {
                            const next = { ...prev };
                            if (!value.trim()) {
                              delete next[String(position)];
                            } else {
                              next[String(position)] = value.trim();
                            }
                            return next;
                          });
                        }}
                        placeholder="Например: R1, R16"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                  ))}
                  {Number(formData.playerCount) < 2 && (
                    <p className="text-xs text-gray-500">Укажите количество игроков, чтобы настроить коды.</p>
                  )}
                </div>
              </div>

            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={handleSave}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
              >
                Сохранить
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingTemplate(null);
                  setMixedStages([]);
                  setPositionServiceCodes({});
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BracketTemplates;

